import { useRequest } from 'ahooks';
import { useAtom } from 'jotai';
import * as React from 'react';
import { getBootProcessing, postBootProcessing } from '../service';
import type { BootProcessingConfigType, Store } from '../types/api';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { find, debounce } from 'lodash-es';
import { BootInProcess } from '../types/api';
import { configurationId } from '../atoms/configuration';
import useLLM from './useLLM';

const TAG = 3; // 在开发环境需要清理缓存时，修改这个值即可
// content 不需要放到state或atom中, 直接更新这个内存即可
// unConfigBootStore, updateTimestamp, tag 通用字段，不用管
type ContentCacheType = {
  unConfigBootStore: Store[]; // 未配置的店铺
  updateTimestamp: number; // 更新时间戳
  tag: number; // 标签
  [key: string]: any;
};
let ContentCache: ContentCacheType = {} as ContentCacheType;
let RestConfig: Omit<BootInProcess[number], 'content'> = {} as Omit<
  BootInProcess[number],
  'content'
>;

export const getContentCache = () => ContentCache;
export const getRestConfig = () => RestConfig;

export const getCCV = (key: string): any => {
  return getContentCache()[key];
};
export const useGetCCV = (key: string): any => {
  const ref = React.useRef(getCCV(key));
  return ref.current;
};
export const setCCV: SetCCV = (key, value) => {
  if (typeof key === 'string' && typeof value !== 'function') {
    ContentCache[key] = value;
  }
  if (typeof key === 'string' && typeof value === 'function') {
    ContentCache[key] = value(ContentCache[key]);
  }
  if (typeof key === 'object') {
    Object.assign(ContentCache, key);
  }
  ContentCache.updateTimestamp = Date.now();
  ContentCache.tag = TAG;
};

type SetCCV = {
  (key: string, callback: (value: any) => any): void;
  (key: string, value: any): void;
  (key: Record<string, any>, value: undefined): void;
};

export const useSubmitCCV = () => {
  const { run: submitCCV } = useRequest((value) => postBootProcessing(value), {
    manual: true,
    onError: (err) => {
      console.error('submitCCV error', err);
    },
  });
  const fn = React.useCallback(
    (key?, value?) => {
      setCCV(key, value);
      submitCCV({
        ...getRestConfig(),
        content: safeStringify(getContentCache()),
      });
    },
    [submitCCV],
  );
  return debounce(fn, 1000);
};

export const useRefreshConfiguration = (type: BootProcessingConfigType, skip?: boolean) => {
  const [, setId] = useAtom(configurationId);
  const { t } = useTranslate();
  const LLM = useLLM();
  const { loading, error } = useRequest(
    () => {
      if (skip) {
        return Promise.resolve([]);
      }
      return getBootProcessing(LLM).then((data) => {
        const matchConfig = find(data, ({ configType }) => configType === type);
        if (matchConfig) {
          const { content, configurationId, ...rest } = matchConfig;
          try {
            const c = JSON.parse(content);
            if (c.tag === TAG) {
              ContentCache = JSON.parse(content);
            }
          } catch (_) {}

          setId(configurationId);
          RestConfig = {
            ...rest,
            configurationId,
          };
        } else {
          throw new Error('Config Not Found');
        }
        return data;
      });
    },
    {
      onError: () => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      },
    },
  );

  // useUnmount(() => {
  //   removeCache();
  // });

  return {
    loading,
    error,
  };
};

export const useUpdateStepName = () => {
  return React.useCallback(
    (
      stepName: BootInProcess[number]['stepName'],
      configType?: BootInProcess[number]['configType'],
    ) => {
      const RestConfig = getRestConfig();
      RestConfig.stepName = stepName;
      if (configType) {
        RestConfig.configType = configType;
      }
      if (RestConfig.configurationId) {
        postBootProcessing({
          ...RestConfig,
          stepName,
          content: safeStringify(ContentCache),
        });
      }
    },
    [],
  );
};

export const removeCache = (excludes: string[] = []) => {
  const keys = Object.keys(ContentCache);
  const removeKeys = keys.filter((k) => !excludes.includes(k));
  removeKeys.forEach((removeKey) => {
    delete ContentCache[removeKey];
  });
  RestConfig = {} as Omit<BootInProcess[number], 'content'>;
};

export const cleanCache = () => {
  postBootProcessing({
    ...getRestConfig(),
    stepName: 'WELCOME_MESSAGE',
    content: '',
  });

  removeCache();
};

window.cleanBootCache = cleanCache;

export const getCacheStepName = () => {
  return RestConfig.stepName;
};

function safeStringify(obj: any) {
  try {
    return JSON.stringify(obj);
  } catch (_) {
    return '';
  }
}
